import React, { useMemo, useState } from 'react';
import { AppData, ExpenseCategory, Expense, PaymentMode } from '../types';
import { Plus, Trash2, PieChart as PieIcon, Briefcase, TrendingUp, DollarSign, Calendar, ChevronLeft, ChevronRight, Filter, Pencil, X, Lock } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', '#FF6B6B', '#4ECDC4'];

const OPS_CATEGORIES = [
  ExpenseCategory.RENT,
  ExpenseCategory.UTILITIES,
  ExpenseCategory.SALARY_FIXED,
  ExpenseCategory.MARKETING,
  ExpenseCategory.MAINTENANCE,
  ExpenseCategory.FOOD_PERSONAL,
  ExpenseCategory.INVESTMENT,
  ExpenseCategory.MISC
];

// Reused Modal locally to avoid complex exports if not needed elsewhere immediately
const OpsDeleteModal: React.FC<{ isOpen: boolean; onClose: () => void; onConfirm: () => void; t: any }> = ({ isOpen, onClose, onConfirm, t }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
        <div className="flex items-center gap-3 text-red-600 mb-4">
          <div className="bg-red-100 p-2 rounded-full"><Lock size={20} /></div>
          <h3 className="text-lg font-bold">{t.delete}?</h3>
        </div>
        <input 
            type="password" 
            placeholder={t.password}
            className="w-full border border-gray-300 p-3 rounded-lg mb-2 focus:ring-2 focus:ring-red-500 outline-none"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            autoFocus
        />
        {error && <p className="text-xs text-red-600 font-medium mb-3">{error}</p>}
        <div className="flex gap-3 mt-4">
             <button onClick={() => { onClose(); setPassword(''); }} className="flex-1 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">{t.evt_cancel_btn}</button>
             <button onClick={() => {
                if (password.toLowerCase() === 'ganesha') {
                    onConfirm();
                    onClose();
                    setPassword('');
                } else {
                    setError('Incorrect password');
                }
             }} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">{t.confirm}</button>
        </div>
      </div>
    </div>
  );
};

export const OperationsModule: React.FC<{ data: AppData }> = ({ data }) => {
  const { t } = data;
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Delete State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    amount: '',
    category: ExpenseCategory.UTILITIES,
    mode: PaymentMode.UPI,
    paidBy: 'Owner',
    description: '',
    date: new Date().toISOString().slice(0, 10)
  });

  const handleMonthChange = (direction: 'prev' | 'next') => {
    const date = new Date(selectedMonth + '-01'); 
    if (direction === 'prev') date.setMonth(date.getMonth() - 1);
    else date.setMonth(date.getMonth() + 1);
    setSelectedMonth(date.toISOString().slice(0, 7));
  };

  const openAddModal = () => {
    setEditingId(null);
    setFormData({
        amount: '',
        category: ExpenseCategory.UTILITIES,
        mode: PaymentMode.UPI,
        paidBy: 'Owner',
        description: '',
        date: new Date().toISOString().slice(0, 10)
    });
    setShowModal(true);
  };

  const openEditModal = (expense: Expense) => {
    setEditingId(expense.id);
    setFormData({
        amount: expense.amount.toString(),
        category: expense.category,
        mode: expense.mode,
        paidBy: expense.paidBy,
        description: expense.description || '',
        date: expense.date
    });
    setShowModal(true);
  };

  const handleSave = () => {
    if(Number(formData.amount) <= 0) return alert("Please enter a valid amount");
    if(!formData.date) return alert("Date is required");

    if (editingId) {
        data.deleteExpense(editingId);
        const updatedExpense: Expense = {
            id: editingId,
            eventId: null,
            category: formData.category,
            amount: Number(formData.amount),
            mode: formData.mode,
            paidBy: formData.paidBy,
            description: formData.description,
            date: formData.date
        };
        data.addExpense(updatedExpense);
    } else {
        const newExpense: Expense = {
            id: Math.random().toString(36).substr(2, 9),
            eventId: null,
            category: formData.category,
            amount: Number(formData.amount),
            mode: formData.mode,
            paidBy: formData.paidBy,
            description: formData.description,
            date: formData.date
        };
        data.addExpense(newExpense);
    }
    setShowModal(false);
  };

  const handleDeleteRequest = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setItemToDelete(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
      if (itemToDelete) {
          data.deleteExpense(itemToDelete);
          setItemToDelete(null);
      }
  };

  // --- Derived Data ---
  const allOpsExpenses = useMemo(() => data.expenses.filter(e => e.eventId === null), [data.expenses]);
  const filteredExpenses = useMemo(() => {
    return allOpsExpenses.filter(e => e.date.startsWith(selectedMonth));
  }, [allOpsExpenses, selectedMonth]);

  const stats = useMemo(() => {
    const investment = filteredExpenses
        .filter(e => e.category === ExpenseCategory.INVESTMENT)
        .reduce((sum, e) => sum + e.amount, 0);
        
    const operational = filteredExpenses
        .filter(e => e.category !== ExpenseCategory.INVESTMENT)
        .reduce((sum, e) => sum + e.amount, 0);
        
    return { investment, operational, total: investment + operational };
  }, [filteredExpenses]);

  const categoryData = useMemo(() => {
    const map: Record<string, number> = {};
    filteredExpenses.forEach(e => {
        if(e.category !== ExpenseCategory.INVESTMENT) {
            map[e.category] = (map[e.category] || 0) + e.amount;
        }
    });
    return Object.keys(map)
        .map(name => ({ name, value: map[name] }))
        .sort((a, b) => b.value - a.value); 
  }, [filteredExpenses]);

  const formattedMonth = new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <OpsDeleteModal 
        isOpen={deleteModalOpen} 
        onClose={() => setDeleteModalOpen(false)} 
        onConfirm={confirmDelete} 
        t={t}
      />

      {/* Top Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
           <h2 className="text-2xl font-bold text-gray-800">{t.ops_title}</h2>
           <p className="text-gray-500 text-sm">{t.ops_subtitle}</p>
        </div>
        
        <div className="flex items-center gap-4">
            <div className="flex items-center bg-white rounded-lg shadow-sm border border-gray-200 p-1">
                <button onClick={() => handleMonthChange('prev')} className="p-2 hover:bg-gray-100 rounded-md text-gray-600">
                    <ChevronLeft size={20} />
                </button>
                <div className="px-4 font-semibold text-gray-700 w-36 text-center select-none">
                    {formattedMonth}
                </div>
                <button onClick={() => handleMonthChange('next')} className="p-2 hover:bg-gray-100 rounded-md text-gray-600">
                    <ChevronRight size={20} />
                </button>
            </div>

            <button 
                onClick={openAddModal}
                className="bg-purple-600 text-white px-4 py-2.5 rounded-lg flex items-center gap-2 hover:bg-purple-700 transition shadow-md font-medium"
            >
                <Plus size={18} /> <span className="hidden md:inline">{t.ops_add}</span>
            </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 relative overflow-hidden">
            <div className="absolute right-0 top-0 h-full w-1 bg-purple-500"></div>
            <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
                    <Briefcase size={20} />
                </div>
                <p className="text-sm font-medium text-gray-500 uppercase">{t.ops_burn}</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">₹{stats.operational.toLocaleString()}</p>
        </div>

        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 relative overflow-hidden">
            <div className="absolute right-0 top-0 h-full w-1 bg-blue-500"></div>
            <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                    <TrendingUp size={20} />
                </div>
                <p className="text-sm font-medium text-gray-500 uppercase">{t.ops_invest}</p>
            </div>
            <p className="text-2xl font-bold text-blue-700">₹{stats.investment.toLocaleString()}</p>
        </div>

        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 relative overflow-hidden">
            <div className="absolute right-0 top-0 h-full w-1 bg-gray-500"></div>
            <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-gray-100 rounded-lg text-gray-600">
                    <DollarSign size={20} />
                </div>
                <p className="text-sm font-medium text-gray-500 uppercase">{t.ops_outflow}</p>
            </div>
            <p className="text-2xl font-bold text-gray-800">₹{stats.total.toLocaleString()}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Chart Section */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 lg:col-span-1 flex flex-col">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <PieIcon size={18} className="text-purple-600" />
                <span>{t.ops_breakdown}</span>
            </h3>
            
            <div className="flex-1 min-h-[250px] relative">
                {categoryData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={categoryData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                paddingAngle={5}
                                dataKey="value"
                            >
                            {categoryData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                            </Pie>
                            <RechartsTooltip formatter={(value: number) => `₹${value.toLocaleString()}`} />
                            <Legend layout="vertical" verticalAlign="bottom" wrapperStyle={{fontSize: '12px'}} />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                        <PieIcon size={48} className="mb-2 opacity-20" />
                        <p className="text-sm">No operational expenses</p>
                        <p className="text-xs">in {formattedMonth}</p>
                    </div>
                )}
            </div>
        </div>

        {/* Transaction List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden lg:col-span-2 flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <Calendar size={18} className="text-gray-500" />
                    <h3 className="font-bold text-gray-800">{t.ops_log}</h3>
                </div>
                <span className="text-xs font-medium text-gray-500 bg-white border border-gray-200 px-3 py-1 rounded-full">
                    {filteredExpenses.length} Records
                </span>
            </div>
            
            <div className="overflow-auto flex-1 max-h-[500px]">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500 font-semibold sticky top-0 z-10">
                        <tr>
                            <th className="px-6 py-3 border-b border-gray-200">{t.date}</th>
                            <th className="px-6 py-3 border-b border-gray-200">{t.category}</th>
                            <th className="px-6 py-3 border-b border-gray-200">{t.desc}</th>
                            <th className="px-6 py-3 border-b border-gray-200 text-right">{t.amount}</th>
                            <th className="px-6 py-3 border-b border-gray-200 text-right">{t.actions}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredExpenses.length > 0 ? (
                            filteredExpenses.sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(e => (
                                <tr 
                                    key={e.id} 
                                    className="hover:bg-purple-50 transition cursor-pointer group"
                                    onClick={() => openEditModal(e)}
                                >
                                    <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                                        <div className="font-medium text-gray-900">{e.date}</div>
                                        <div className="text-xs text-gray-400">{e.paidBy} ({e.mode})</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                                            e.category === ExpenseCategory.INVESTMENT 
                                                ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                                                : 'bg-gray-100 text-gray-700 border border-gray-200'
                                        }`}>
                                            {e.category}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-800 max-w-xs truncate">
                                        {e.description || <span className="text-gray-400 italic">No description</span>}
                                    </td>
                                    <td className="px-6 py-4 text-right font-bold text-gray-800">
                                        ₹{e.amount.toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button 
                                                onClick={(evt) => { evt.stopPropagation(); openEditModal(e); }}
                                                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                                                title="Edit"
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button 
                                                onClick={(evt) => handleDeleteRequest(e.id, evt)} 
                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                                title="Delete"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5} className="px-6 py-12 text-center">
                                    <div className="flex flex-col items-center justify-center text-gray-400">
                                        <Filter size={32} className="mb-2 opacity-20" />
                                        <p className="text-sm font-medium">No records found for {formattedMonth}</p>
                                        <button 
                                            onClick={openAddModal}
                                            className="mt-2 text-purple-600 text-sm hover:underline"
                                        >
                                            {t.ops_add}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md transform transition-all scale-100 overflow-hidden">
             {/* Modal Header */}
             <div className="bg-purple-600 px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    {editingId ? <Pencil size={18} /> : <Plus size={18} />}
                    {editingId ? t.edit : t.ops_add}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-white hover:text-purple-200 transition">
                    <X size={20} />
                </button>
             </div>
             
             <div className="p-6 space-y-4">
               <div>
                 <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t.category}</label>
                 <select 
                    className="w-full border border-gray-300 p-3 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-purple-500 outline-none transition"
                    value={formData.category}
                    onChange={e => setFormData({...formData, category: e.target.value as ExpenseCategory})}
                 >
                   {/* Filter Categories to show only Ops relevant ones */}
                   {OPS_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                 </select>
               </div>
               
               <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t.amount}</label>
                     <div className="relative">
                        <span className="absolute left-3 top-3 text-gray-400">₹</span>
                        <input 
                            type="number" 
                            className="w-full border border-gray-300 p-3 pl-8 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none transition"
                            placeholder="0.00"
                            value={formData.amount}
                            onChange={e => setFormData({...formData, amount: e.target.value})} 
                            autoFocus
                        />
                     </div>
                   </div>

                   <div>
                     <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t.date}</label>
                     <input 
                        type="date" 
                        className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                        value={formData.date}
                        onChange={e => setFormData({...formData, date: e.target.value})} 
                     />
                   </div>
               </div>

               <div>
                 <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t.desc}</label>
                 <input 
                    type="text" 
                    className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                    placeholder="E.g. Shop Rent, Electricity Bill..."
                    value={formData.description}
                    onChange={e => setFormData({...formData, description: e.target.value})} 
                 />
               </div>

               <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t.mode}</label>
                     <select 
                        className="w-full border border-gray-300 p-3 rounded-lg bg-gray-50"
                        value={formData.mode}
                        onChange={e => setFormData({...formData, mode: e.target.value as PaymentMode})}
                     >
                       {Object.values(PaymentMode).map(m => <option key={m} value={m}>{m}</option>)}
                     </select>
                   </div>
                   
                   <div>
                     <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t.paid_by}</label>
                     <input 
                        type="text" 
                        className="w-full border border-gray-300 p-3 rounded-lg"
                        value={formData.paidBy}
                        onChange={e => setFormData({...formData, paidBy: e.target.value})} 
                     />
                   </div>
               </div>

               <button 
                onClick={handleSave} 
                className="w-full mt-4 bg-purple-600 text-white py-3 rounded-lg font-bold hover:bg-purple-700 transition shadow-lg shadow-purple-200 flex justify-center items-center gap-2"
               >
                {editingId ? <Pencil size={18} /> : <Plus size={18} />}
                {editingId ? t.evt_save_btn : t.ops_add}
               </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};